// apps/web/src/wizard/steps/A6ConfirmStep.tsx — Phase 4 plan 04-03.
//
// Review + submit step (A6 in the wireframe, step 5 of 6 in the
// progress segments — the A7 confirmation page is the 6th).
//
// On click of "Verbindlich reservieren" the component fires
// `Mutation.createReservation` with the consentAt captured at
// the A5 checkbox-click moment (NOT a fresh `new Date()` here —
// D-15 / DSG-05). The Turnstile widget is mounted; in dev
// `getToken()` returns `""` and the server's dev-bypass returns
// true (D-26).
//
// On success, the response (reservationNumber + selfServiceToken
// + selfServiceUrl + expiresAt) is passed to A7 via
// `navigate('/reservierung/bestaetigung', { state: { ... } })`.
// The A7 page strips the token from the URL bar via
// `history.replaceState` (T-04-25 / OQ-7).
//
// On error, `localizeError` reads `extensions.messageKey` first,
// then falls back to a code-based mapping in
// `apps/web/src/wizard/validation.ts`. The manual-mode case
// (D-07) shows a different submit label + a note; the success
// page distinguishes "bestätigt" vs "angefragt" via the
// `reservationNumber` prefix (R- vs A-).

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Turnstile, type TurnstileHandle } from "../../components/turnstile";
import {
  createReservation,
  getRestaurant,
  type CreateReservationResult,
} from "../../lib/api";
import { buildCreateReservationInput } from "../schema";
import { localizeError, mapErrorCode } from "../validation";
import { useWizardSlice, resetWizard } from "../state";

export function A6ConfirmStep(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language.startsWith("en") ? "en" : "de";
  const turnstileRef = useRef<TurnstileHandle>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const serviceDate = useWizardSlice((s) => s.serviceDate);
  const partySize = useWizardSlice((s) => s.partySize);
  const selectedSlot = useWizardSlice((s) => s.selectedSlot);
  const guest = useWizardSlice((s) => s.guest);

  const restaurantQuery = useQuery({
    queryKey: ["wizard-restaurant-confirm"],
    queryFn: () => getRestaurant(),
    staleTime: 60_000,
  });
  const isManual = restaurantQuery.data?.confirmationMode === "manual";

  const mutation = useMutation<CreateReservationResult, Error, string>({
    mutationFn: async (captchaToken: string) => {
      if (!serviceDate || !selectedSlot) {
        throw new Error("missing-wizard-state");
      }
      const built = buildCreateReservationInput({
        serviceDate,
        slotDefinitionId: selectedSlot.slotDefinitionId,
        startsAt: selectedSlot.startsAt,
        durationMinutes: selectedSlot.durationMinutes,
        partySize,
        areaId: null,
        guestName: guest.name,
        guestEmail: guest.email,
        guestPhone: guest.phone,
        consentText: guest.consentText || "",
        consentAt: guest.consentAt || new Date().toISOString(),
      });
      if (!built.ok || !built.value) {
        throw new Error(built.issues[0]?.key ?? "wizard.a6.errors.invalidSlot");
      }
      return createReservation({
        input: built.value,
        captchaToken,
      });
    },
  });

  // Pre-flight: no service date → A1, no slot → A2, no consent
  // text/At → A5.
  if (!serviceDate) {
    navigate("/reservieren/datum", { replace: true });
    return <></>;
  }
  if (!selectedSlot) {
    navigate("/reservieren/zeit", { replace: true });
    return <></>;
  }
  if (!guest.consentAt || !guest.consentText) {
    navigate("/reservierung/kontakt", { replace: true });
    return <></>;
  }

  const summary = {
    date: formatLongDate(serviceDate, locale),
    time: selectedSlot.startTime,
    duration: formatDuration(selectedSlot.durationMinutes, locale),
    party: t("wizard.a7.partySize", { count: partySize }),
    contact: guest.email?.trim() || guest.phone?.trim() || guest.name,
    consent: guest.consentText,
  };

  function back() {
    navigate("/reservierung/kontakt");
  }

  async function submit() {
    setErrorKey(null);
    setSubmitting(true);
    try {
      const captchaToken = turnstileRef.current?.getToken() ?? "";
      const result = await mutation.mutateAsync(captchaToken);
      navigate("/reservierung/bestaetigung", {
        state: {
          reservationNumber: result.reservationNumber,
          selfServiceToken: result.selfServiceToken,
          selfServiceUrl: result.selfServiceUrl,
          expiresAt: result.expiresAt,
          confirmationMode: isManual ? "manual" : "auto",
        },
      });
    } catch (err) {
      // graphql-request throws a `ClientError` whose extensions live
      // on `err.response.errors[0].extensions`, NOT on `err.extensions`
      // directly. Without this extraction the `localizeError` call sees
      // a `null` extensions bag and falls through to `errors.network`
      // ("Verbindung fehlgeschlagen") for every GraphQL error — which
      // is misleading: a SLOT_FULL should show "Zeitpunkt ausgebucht",
      // not "Verbindung fehlgeschlagen". This wrapper flattens the
      // ClientError shape to the { extensions: { code, messageKey } }
      // contract `localizeError` already expects.
      const clientError = err as {
        response?: {
          errors?: Array<{
            extensions?: { code?: string; messageKey?: string };
          }>;
        };
      };
      const firstError = clientError.response?.errors?.[0];
      // Coerce `undefined` to `{}` so the synthesized object literal
      // matches `localizeError`'s `{ extensions?: {...} }` contract —
      // TypeScript with `exactOptionalPropertyTypes: true` rejects the
      // `extensions: undefined` shape. `localizeError` only reads
      // `extensions.messageKey` / `extensions.code`, both already
      // optional, so the empty object safely falls through to the
      // unknown-code branch in `mapErrorCode`.
      const key = localizeError(
        firstError
          ? { extensions: firstError.extensions ?? {} }
          : ((err as {
              extensions?: { code?: string; messageKey?: string };
            }) ?? null),
        t,
      );
      setErrorKey(key);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a6-confirm-step"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
          data-testid="a6-header"
        >
          <button
            type="button"
            onClick={back}
            aria-label={t("wizard.a6.back")}
            data-testid="a6-back"
            className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-1">
            <h1
              className="font-heading text-2xl md:text-3xl"
              data-testid="a6-heading"
            >
              {t("wizard.a6.title")}
            </h1>
            <p
              className="text-sm text-text/70 mt-0.5"
              data-testid="a6-subtitle"
            >
              {t("wizard.a6.subtitle")}
            </p>
          </div>
        </header>

        <div className="px-4 pt-4">
          <p className="text-xs text-text/70 mb-2" data-testid="a6-meta">
            Schritt 5 von 6
          </p>
          <WizardProgress current={5} />
        </div>

        <div className="px-4 pt-4 pb-4">
          <dl
            className="divide-y divide-divider border border-divider rounded-md"
            data-testid="a6-summary-card"
          >
            <Row
              label={t("wizard.a6.summary.date")}
              value={summary.date}
              testid="a6-summary-date"
            />
            <Row
              label={t("wizard.a6.summary.time")}
              value={summary.time}
              testid="a6-summary-time"
            />
            <Row
              label={t("wizard.a6.summary.duration")}
              value={summary.duration}
              testid="a6-summary-duration"
            />
            <Row
              label={t("wizard.a6.summary.party")}
              value={summary.party}
              testid="a6-summary-party"
            />
            <Row
              label={t("wizard.a6.summary.contact")}
              value={summary.contact}
              testid="a6-summary-contact"
            />
            <Row
              label={t("wizard.a6.summary.consent")}
              value={
                <details data-testid="a6-summary-consent-details">
                  <summary className="cursor-pointer text-sm text-accent-700">
                    {t("wizard.a6.summary.consent")}
                  </summary>
                  <p
                    className="text-sm text-text/80 mt-2"
                    data-testid="a6-summary-consent"
                  >
                    {summary.consent}
                  </p>
                </details>
              }
              testid="a6-summary-consent-row"
            />
          </dl>
        </div>

        {isManual && (
          <div
            className="mx-4 mb-4 text-sm text-text bg-surface border border-divider rounded-md p-3"
            data-testid="a6-manual-note"
          >
            {t("wizard.a6.manualNote")}
          </div>
        )}

        <div className="px-4 pb-4">
          <Turnstile
            ref={turnstileRef}
            onToken={() => {
              /* the token is read imperatively on submit */
            }}
            onExpire={() => {
              setErrorKey(t("errors.turnstileFailed"));
            }}
          />
        </div>

        {errorKey && (
          <div
            role="alert"
            className="mx-4 mb-4 text-sm text-red-700 bg-surface border border-red-500 rounded-md p-3"
            data-testid="a6-error"
          >
            {errorKey}
          </div>
        )}

        <div className="px-4 pb-6 flex gap-2">
          <button
            type="button"
            onClick={back}
            className="min-h-[44px] px-4 py-2 rounded-md border border-divider text-text"
            data-testid="a6-back-bottom"
          >
            {t("wizard.a6.back")}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            data-testid="a6-submit"
            className={`flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border ${
              submitting
                ? "border-divider text-text/40"
                : "border-accent text-accent-700 hover:bg-accent-100"
            }`}
          >
            {submitting && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            <span>
              {submitting
                ? t("wizard.a6.submitting")
                : isManual
                  ? t("wizard.a6.submitManual")
                  : t("wizard.a6.submit")}
            </span>
          </button>
        </div>

        {mutation.isError && (
          <p className="sr-only" role="status">
            {t(mapErrorCode(undefined))}
          </p>
        )}

        {/* Dev-only: a noop-reset button hidden from the public
            surface. Helpful for the A7 test surface which clears
            the wizard state on "Neue Reservierung" — that path
            already calls resetWizard() from the A7 page. */}
        {import.meta.env.MODE !== "production" && (
          <button
            type="button"
            data-testid="a6-reset-dev"
            onClick={() => resetWizard()}
            className="sr-only"
          >
            reset
          </button>
        )}
      </div>
    </main>
  );
}

function Row({
  label,
  value,
  testid,
}: {
  label: string;
  value: React.ReactNode;
  testid: string;
}): JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-2"
      data-testid={testid}
    >
      <dt className="text-xs text-text/70 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-text text-right">{value}</dd>
    </div>
  );
}

function WizardProgress({ current }: { current: number }): JSX.Element {
  const { t } = useTranslation();
  return (
    <ol
      role="list"
      aria-label={t("reservation.wizard.progressAria", {
        current,
        total: 6,
      })}
      className="flex gap-1 mb-4"
      data-testid="a6-wizard-progress"
      data-wizard-current={current}
    >
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <li
          key={n}
          aria-current={n === current ? "step" : undefined}
          className={`h-[3px] flex-1 rounded-sm ${
            n <= current ? "bg-accent" : "bg-divider"
          }`}
          data-testid={`a6-wizard-segment-${n}`}
          data-filled={n <= current}
        />
      ))}
    </ol>
  );
}

function formatLongDate(iso: string, locale: "de" | "en"): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const dt = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(dt);
}

function formatDuration(minutes: number, locale: "de" | "en"): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (locale === "en") {
    if (hours === 0) return `${mins} minutes`;
    if (mins === 0) return hours === 1 ? "1 hour" : `${hours} hours`;
    return `${hours} hour ${mins} minutes`;
  }
  if (hours === 0) return `${mins} Minuten`;
  if (mins === 0) return hours === 1 ? "1 Stunde" : `${hours} Stunden`;
  return `${hours} Stunde ${mins} Minuten`;
}
